import { Button, CloseIconThin } from "@cmsgov/ds-medicare-gov";
import React, { FunctionComponent, useContext } from "react";
import { Plan } from "../../../@types";
import { AppStore } from "../../../app/store";
import { EnrollButtonForm } from "../../../components";
import {
  useTranslate,
  isCurrentPlan,
  sendTealiumEvent,
  $$,
  isMapdPlanType,
  isMaPlanType,
  shouldSuppressDrugPricing,
  shouldSuppressPlanBenefits,
} from "../../../helpers";
import { getPlanName } from "../../../helpers/planHelpers";
import { getPlanNameElementId } from "./planHeaderHelpers";

const PlanHeader: FunctionComponent<{
  handleGoToPlan: () => void;
  noLeftBorder?: boolean;
  onPlanRemove?: (plan: Plan) => void;
  plan: Plan;
  planNameId?: string;
}> = ({
  handleGoToPlan,
  noLeftBorder,
  onPlanRemove,
  plan,
  planNameId = getPlanNameElementId(plan),
}) => {
  const t = useTranslate();
  const {
    state: { currentCoverage, nextYearCoverage, language },
  } = useContext(AppStore.AppContext);
  const aria_string = `${t("plan_card.enroll_in")} ${getPlanName(
    plan,
    language
  )}`;

  const currentPlan =
    isCurrentPlan(currentCoverage, plan) ||
    isCurrentPlan(nextYearCoverage, plan);

  const planName = getPlanName(plan, language);

  return (
    <div
      className={`PlanComparePage__planHeader${
        noLeftBorder ? " ds-u-border-left--0" : ""
      }`}
    >
      <div className="ds-u-lg-padding-x--3">
        <div>
          <h3 className="ds-u-margin-bottom--1 ds-u-color--primary ds-u-display--flex ds-u-justify-content--between">
            <span
              id={planNameId}
              className="PlanComparePage__planHeaderPlanName e2e-pc-plan-name"
            >
              {planName}
            </span>
            {onPlanRemove && (
              <Button
                variation="transparent"
                size="small"
                className="PlanComparePage__close mct-hide-print"
                aria-label={`Remove ${planName} plan`}
                onClick={() => onPlanRemove(plan)}
              >
                <CloseIconThin />
              </Button>
            )}
          </h3>
        </div>
        <PlanPremium plan={plan} />
        <div className="ds-u-margin-bottom--3 mct-hide-print">
          <div className="ds-l-row">
            {!currentPlan && (
              <EnrollButtonForm
                plan={plan}
                aria_string={aria_string}
                tealiumEventAction="Find Plans - Compare"
                buttonClassName="ds-c-button--primary-alt mct-u-width--full"
                dialogClassName="PlanComparePage__enrollDialog ds-l-col--12 ds-l-sm-col--6 ds-l-lg-col--auto"
              />
            )}
            <div className="ds-l-col--12 ds-l-sm-col--6 ds-l-lg-col--auto">
              <Button
                onClick={(): void => {
                  // istanbul ignore next: tested elsewhere
                  sendTealiumEvent(
                    "Find Plans - Compare",
                    `Plan Click: ${planName}`
                  );
                  handleGoToPlan();
                }}
                className="PlanComparePage__planDetailsBtn e2e-pc-plan-details-button mct-u-width--full"
                aria-label={"Plan details for " + planName}
              >
                {t("plan_card.plan_details")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {currentPlan && (
        <div className="PlanComparePage__currentPlanBanner">
          {t("plan_card.your_current_plan")}
        </div>
      )}
    </div>
  );
};

const PlanPremium: FunctionComponent<{ plan: Plan }> = ({ plan }) => {
  const t = useTranslate();
  let label;

  if (isMapdPlanType(plan.plan_type)) {
    label = t("plan_terms.monthly_plan_premium_mapd");
  } else if (isMaPlanType(plan.plan_type)) {
    label = t("plan_card.monthly_premium_ma");
  } else {
    label = t("plan_terms.monthly_plan_premium_pdp");
  }

  return (
    <div className="PlanComparePage__planPremium">
      <p className="ds-text-heading--lg ds-u-margin-bottom--1">
        {shouldSuppressDrugPricing(plan) || shouldSuppressPlanBenefits(plan)
          ? t("suppressed_plan_benefit")
          : $$(plan.calculated_monthly_premium)}
      </p>{" "}
      <p className="ds-u-margin--0">{label}</p>
    </div>
  );
};

export default PlanHeader;
